import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { productService } from '../services/productService';
import ProductCard from '../components/Products/ProductCard';
import BannerCarousel from '../components/Common/BannerCarousel';

const findCategoryById = (categories, id) => {
  for (let cat of categories) {
    if (cat.categoryId === Number(id)) return cat;
    if (cat.children && cat.children.length > 0) {
      const found = findCategoryById(cat.children, id);
      if (found) return found;
    }
  }
  return null;
};

const HorizontalScrollRow = ({ children }) => {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    
    setShowLeft(scrollLeft > 0);
    setShowRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 2);
  };

  useEffect(() => {
    const timeout = setTimeout(handleScroll, 150);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', handleScroll);
    };
  }, [children]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.75; // Scroll 75% of container width
      scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative w-full group">
      {showLeft && (
        <button 
          onClick={() => scroll('left')} 
          className="hidden md:flex absolute -left-4 top-[40%] -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white border border-gray-200 items-center justify-center text-[#0F2F63] shadow-lg hover:bg-gray-50 transition-all"
        >
          <i className="fas fa-chevron-left"></i>
        </button>
      )}
      
      <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto gap-3 md:gap-4 w-full hide-scrollbar pb-4 snap-x">
        <style>{`.hide-scrollbar::-webkit-scrollbar { display: none !important; } .hide-scrollbar { -ms-overflow-style: none !important; scrollbar-width: none !important; }`}</style>
        {children}
      </div>

      {showRight && (
        <button 
          onClick={() => scroll('right')} 
          className="hidden md:flex absolute -right-4 top-[40%] -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white border border-gray-200 items-center justify-center text-[#0F2F63] shadow-lg hover:bg-gray-50 transition-all"
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      )}
    </div>
  );
};

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [categorySections, setCategorySections] = useState([]);
  const [dynamicGroups, setDynamicGroups] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState(null);

  const isStandard = location.pathname.includes('standard');
  const orderType = isStandard ? 'STANDARD' : 'QUICK';

  const queryParams = new URLSearchParams(location.search);
  const selectedCategoryQuery = queryParams.get('category');
  const selectedCategoryId = queryParams.get('categoryId');

  const fallbackImg = 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=300&q=80';

  useEffect(() => {
    const fetchCatalogAndBannersData = async () => {
      try {
        setLoadingProducts(true);
        
        let catalogPromise;
        let bannersPromise;

        if (selectedCategoryId) {
          catalogPromise = (async () => {
            const allCats = await productService.getCategories(orderType);
            const targetCat = findCategoryById(allCats, selectedCategoryId);
            
            let groups = [];
            if (targetCat && targetCat.children && targetCat.children.length > 0) {
              groups = targetCat.children;
            }
            
            const productsData = await productService.getProductsByCategory(selectedCategoryId, orderType);
            const catNameParam = queryParams.get('category') ? queryParams.get('category').replace(/-/g, ' ') : 'Products';
            
            return {
              groups,
              sections: [{
                categoryId: selectedCategoryId,
                categoryName: catNameParam.charAt(0).toUpperCase() + catNameParam.slice(1),
                variants: productsData || []
              }]
            };
          })();
          
          bannersPromise = productService.getBanners('CATEGORY', orderType)
            .then(data => data.filter(b => b.categoryId === Number(selectedCategoryId)));
            
        } else {
          catalogPromise = (async () => {
            const lowPriceData = await productService.getLowPriceVariants(orderType);
            let sections = lowPriceData.categorySections || [];
            
            if (selectedCategoryQuery && selectedCategoryQuery !== 'low-price' && selectedCategoryQuery !== 'all') {
              const formattedQuery = selectedCategoryQuery.replace(/-/g, ' ').toLowerCase();
              sections = sections.filter(sec => sec.categoryName.toLowerCase().includes(formattedQuery));
            }
            
            return { groups: [], sections };
          })();
          
          bannersPromise = productService.getBanners('LOW_PRICE', orderType);
        }

        const [catalogResult, bannersResult] = await Promise.all([
          catalogPromise,
          bannersPromise
        ]);

        const validSections = catalogResult.sections.filter(sec => sec.variants && sec.variants.length > 0);
        
        setDynamicGroups(catalogResult.groups);
        setCategorySections(validSections);
        
        const formattedBanners = (bannersResult || []).map((b) => ({
          id: b.id,
          title: b.title || '',
          image: b.imageUrl,
          redirectUrl: b.redirectUrl,
          bgColor: '#0F2F63' 
        }));
        setBanners(formattedBanners);
        setError(null);
      } catch (err) {
        console.error('Error loading catalog data:', err);
        setError('Failed to load catalog. Please try again.');
      } finally {
        setLoading(false);
        setLoadingProducts(false);
      }
    };

    fetchCatalogAndBannersData();
  }, [location.pathname, orderType, selectedCategoryQuery, selectedCategoryId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 min-h-[500px]">
        <i className="fas fa-spinner fa-spin text-4xl text-[#0F2F63] mb-4"></i>
        <p className="text-gray-500 font-medium">Loading SV Mart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 px-5 bg-red-50 rounded-xl border border-red-200 max-w-lg mx-auto mt-10">
        <i className="fas fa-exclamation-circle text-5xl text-red-500 mb-4"></i>
        <h3 className="text-xl text-red-600 font-bold mb-2">Something went wrong</h3>
        <p className="text-sm text-gray-600 mb-5">{error}</p>
        <button onClick={() => window.location.reload()} className="bg-[#0F2F63] text-white px-6 py-2.5 rounded-lg font-bold hover:bg-[#1a4a8a] transition-colors">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="py-2 md:py-6 px-1 md:px-8 max-w-7xl mx-auto w-full">
      <BannerCarousel banners={banners} />
      
      {loadingProducts ? (
        <div className="flex flex-col items-center justify-center py-16 min-h-[300px]">
          <i className="fas fa-spinner fa-spin text-4xl text-[#0F2F63] mb-4"></i>
          <p className="text-gray-500 font-medium">Loading products...</p>
        </div>
      ) : categorySections.length === 0 && dynamicGroups.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
          <i className="fas fa-box-open text-5xl text-gray-300 mb-4"></i>
          <h3 className="text-xl text-gray-900 font-bold mb-1">No Items Available</h3>
          <p className="text-sm text-gray-500">Check back later for new items.</p>
        </div>
      ) : (
        <div className="w-full">
          
          {dynamicGroups.length > 0 && (
            <div className="w-full mb-6 mt-2">
              {dynamicGroups.map((group) => (
                <div key={group.categoryId} className="mb-10 w-full">
                  <h2 className="text-xl md:text-[22px] font-black text-gray-900 tracking-tight mb-4 px-1">
                    {group.categoryName}
                  </h2>
                  
                  {group.children && group.children.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5 md:gap-4">
                      {group.children.map((sub) => (
                        <div
                          key={sub.categoryId}
                          onClick={() => navigate(`/products?category=${sub.categoryName}&categoryId=${sub.categoryId}&mode=${orderType}`)}
                          className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden flex flex-col"
                        >
                          <div className="w-full aspect-square bg-gray-50 overflow-hidden relative">
                            <img 
                              src={sub.imageUrl || fallbackImg} 
                              alt={sub.categoryName} 
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="p-2 md:p-3 bg-white text-center">
                            <h3 className="text-[11px] md:text-sm font-bold text-gray-800 line-clamp-1">{sub.categoryName}</h3>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {categorySections.map((section) => (
            <div key={section.categoryId} className="mb-10 md:mb-12 w-full">
              <div className="flex justify-between items-center mb-4 md:mb-5 px-1 border-b border-gray-200 pb-2 md:pb-3">
                <h2 className="text-lg md:text-2xl font-black text-gray-900 tracking-tight">
                  {section.categoryName}
                </h2>
                <button 
                  onClick={() => navigate(`/products?category=${section.categoryName}&categoryId=${section.categoryId}&isLowPrice=true&mode=${orderType}`)}
                  className="text-sky-600 hover:text-sky-700 font-bold text-xs md:text-sm transition-colors"
                >
                  View all
                </button>
              </div>
              
              <HorizontalScrollRow>
                {section.variants.slice(0, 9).map((variant) => (
                  <div key={variant.outletVariantId || variant.variantId} className="w-[140px] md:w-[180px] flex-shrink-0 snap-start">
                    <ProductCard 
                      product={variant} 
                      addToCart={addToCart} 
                    />
                  </div>
                ))}
              </HorizontalScrollRow>
              
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HomePage;